import {DocumentPartTemplateData, TaskTemplateData, Template, useAppDispatch} from '@dv5/domain'
import {templatesApi} from '@dv5/api'
import {type MouseEvent, useEffect, useMemo, useRef, useState} from 'react'
import {ErrorDisplayProcessor} from '../ErrorDisplayProcessor'
import {
    getDocumentPartFormData,
    getDocumentTemplateFormData,
    getTaskFormData,
    isDocumentPart,
    isDocumentTemplate,
    isTask,
} from './templateDataForm'
import {
    addPart,
    DocumentPartForm,
    DocumentTemplateFormData,
    movePart,
    removePart,
    toDocumentTemplateData,
    updatePart,
} from './documentTemplateForm'

export type ViewMode = 'view' | 'formEdit' | 'jsonEdit'

interface UseTemplateDataEditorOptions {
    template: Template
    onClose: () => void
    onTemplateUpdated?: (template: Template) => void
}

export function useTemplateDataEditor({template, onClose, onTemplateUpdated}: UseTemplateDataEditorOptions) {
    const dispatch = useAppDispatch()
    const errorProcessor = useMemo(() => new ErrorDisplayProcessor(dispatch), [dispatch])
    const dialogRef = useRef<HTMLDivElement>(null)
    const [displayTemplate, setDisplayTemplate] = useState<Template>(template)

    const docPart = isDocumentPart(template)
    const task = isTask(template)
    const docTemplate = isDocumentTemplate(template)
    const hasFormEditor = docPart || task || docTemplate
    const initialFormData = docPart ? getDocumentPartFormData(template) : { partHeader: '', partContent: '', partType: '' }
    const initialTaskFormData = task ? getTaskFormData(template) : { title: '', description: '', webHookUrl: '' }
    const initialDocTemplateFormData = docTemplate
        ? getDocumentTemplateFormData(template)
        : { sourceDocumentId: '', title: '', parts: [] }

    const [viewMode, setViewMode] = useState<ViewMode>('view')
    const [draftJson, setDraftJson] = useState('')
    const [formPartHeader, setFormPartHeader] = useState(initialFormData.partHeader)
    const [formPartContent, setFormPartContent] = useState(initialFormData.partContent)
    const [formTaskData, setFormTaskData] = useState<TaskTemplateData>(initialTaskFormData)
    const [docTemplateFormData, setDocTemplateFormData] = useState<DocumentTemplateFormData>(initialDocTemplateFormData)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            e.preventDefault()
            if (viewMode === 'jsonEdit') {
                setViewMode(hasFormEditor ? 'formEdit' : 'view')
                setErrorMessage(null)
            } else if (viewMode === 'formEdit') {
                setViewMode('view')
                setErrorMessage(null)
            } else {
                onClose()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [viewMode, hasFormEditor, onClose])

    const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onClose()
        }
    }

    const cancelEdit = () => {
        setErrorMessage(null)
        setViewMode('view')
    }

    const switchToForm = () => {
        setErrorMessage(null)
        setViewMode('formEdit')
    }

    const enterFormEdit = () => {
        setFormPartHeader(initialFormData.partHeader)
        setFormPartContent(initialFormData.partContent)
        setFormTaskData(initialTaskFormData)
        setDocTemplateFormData(initialDocTemplateFormData)
        setErrorMessage(null)
        setViewMode('formEdit')
    }

    const enterJsonEdit = () => {
        let json: string
        if (viewMode === 'formEdit') {
            if (task) {
                json = JSON.stringify(formTaskData, null, 2)
            } else if (docPart) {
                const formData: DocumentPartTemplateData = {
                    partHeader: formPartHeader,
                    partContent: formPartContent,
                    partType: initialFormData.partType,
                }
                json = JSON.stringify(formData, null, 2)
            } else if (docTemplate) {
                const templateData = toDocumentTemplateData({
                    ...docTemplateFormData,
                    sourceDocumentId: initialDocTemplateFormData.sourceDocumentId,
                })
                json = JSON.stringify(templateData, null, 2)
            } else {
                json = JSON.stringify(displayTemplate.templateData, null, 2)
            }
        } else {
            json = JSON.stringify(displayTemplate.templateData, null, 2)
        }
        setDraftJson(json)
        setErrorMessage(null)
        setViewMode('jsonEdit')
    }

    const save = (templateData: Template['templateData']) => {
        setErrorMessage(null)
        setIsSaving(true)
        templatesApi.updateTemplateData(
            displayTemplate.id,
            templateData,
            errorProcessor,
            (updated) => {
                setDisplayTemplate(updated)
                setViewMode('view')
                setIsSaving(false)
                onTemplateUpdated?.(updated)
            }
        ).catch(() => {
            setIsSaving(false)
        })
    }

    const handleSaveFromJson = () => {
        let parsed
        try {
            parsed = JSON.parse(draftJson)
        } catch (e) {
            setErrorMessage('Invalid JSON: ' + (e instanceof Error ? e.message : String(e)))
            return
        }
        save(parsed)
    }

    const handleSaveFromForm = () => {
        const templateData: DocumentPartTemplateData = {
            partHeader: formPartHeader,
            partContent: formPartContent,
            partType: initialFormData.partType,
        }
        save(templateData)
    }

    const handleSaveFromTaskForm = () => {
        const templateData: TaskTemplateData = {
            title: formTaskData.title,
            description: formTaskData.description,
            webHookUrl: formTaskData.webHookUrl,
        }
        save(templateData)
    }

    const setDocTemplateTitle = (title: string) => {
        setDocTemplateFormData(prev => ({...prev, title}))
    }

    const handleDocTemplatePartChange = (index: number, part: DocumentPartForm) => {
        setDocTemplateFormData(prev => ({...prev, parts: updatePart(prev.parts, index, part)}))
    }

    const handleDocTemplateAddPart = () => {
        setDocTemplateFormData(prev => ({...prev, parts: addPart(prev.parts)}))
    }

    const handleDocTemplateRemovePart = (index: number) => {
        setDocTemplateFormData(prev => ({...prev, parts: removePart(prev.parts, index)}))
    }

    const handleDocTemplateMovePart = (from: number, to: number) => {
        setDocTemplateFormData(prev => ({...prev, parts: movePart(prev.parts, from, to)}))
    }

    const handleSaveFromDocumentForm = () => {
        save(toDocumentTemplateData({
            ...docTemplateFormData,
            sourceDocumentId: initialDocTemplateFormData.sourceDocumentId,
        }))
    }

    return {
        displayTemplate,
        docPart,
        task,
        docTemplate,
        hasFormEditor,
        initialFormData,
        initialTaskFormData,
        initialDocTemplateFormData,
        viewMode,
        draftJson,
        setDraftJson,
        formPartHeader,
        setFormPartHeader,
        formPartContent,
        setFormPartContent,
        formTaskData,
        setFormTaskData,
        docTemplateFormData,
        errorMessage,
        isSaving,
        dialogRef,
        handleOverlayClick,
        cancelEdit,
        switchToForm,
        enterFormEdit,
        enterJsonEdit,
        handleSaveFromJson,
        handleSaveFromForm,
        handleSaveFromTaskForm,
        setDocTemplateTitle,
        handleDocTemplatePartChange,
        handleDocTemplateAddPart,
        handleDocTemplateRemovePart,
        handleDocTemplateMovePart,
        handleSaveFromDocumentForm,
    }
}
